<script setup lang="ts">
import { NAV_SECTIONS } from '@/nav'
import { useAuthStore } from '@/stores/auth'
import NavIcon from './NavIcon.vue'

defineProps<{ inDrawer?: boolean }>()

/**
 * The admin backend is not in NAV_SECTIONS, which everybody signed in gets.
 * It appears only for a superuser, and the server refuses its routes to
 * anybody else regardless, so this is tidiness rather than a control.
 */
const auth = useAuthStore()
</script>

<template>
  <nav class="flex flex-col gap-5">
    <div v-for="(section, index) in NAV_SECTIONS" :key="section.header ?? index">
      <p
        v-if="section.header"
        class="px-3 pb-1 text-xs font-medium uppercase tracking-wide text-ink/40"
      >
        {{ section.header }}
      </p>
      <ul class="flex flex-col gap-0.5">
        <li v-for="item in section.items" :key="item.to">
          <RouterLink
            :to="item.to"
            class="flex items-center gap-3 rounded-lg px-3 text-sm text-ink/70 transition-colors hover:bg-mist/40"
            :class="inDrawer ? 'py-2.5' : 'py-2'"
            :exact-active-class="item.exact ? 'bg-mist/60 font-medium text-moss' : ''"
            :active-class="item.exact ? '' : 'bg-mist/60 font-medium text-moss'"
          >
            <NavIcon :name="item.icon" :size="18" class="shrink-0" />
            <span class="truncate">{{ item.label }}</span>
          </RouterLink>
        </li>
      </ul>
    </div>

    <div v-if="auth.me?.isSuperuser">
      <p class="px-3 pb-1 text-xs font-medium uppercase tracking-wide text-ink/40">Admin</p>
      <ul class="flex flex-col gap-0.5">
        <li>
          <RouterLink
            to="/admin/foods"
            class="flex items-center gap-3 rounded-lg px-3 text-sm text-ink/70 transition-colors hover:bg-mist/40"
            :class="inDrawer ? 'py-2.5' : 'py-2'"
            active-class="bg-mist/60 font-medium text-moss"
          >
            <NavIcon name="cupboard" :size="18" class="shrink-0" />
            <span class="truncate">Food library</span>
          </RouterLink>
        </li>
        <li>
          <RouterLink
            to="/admin/foods/review"
            class="flex items-center gap-3 rounded-lg px-3 text-sm text-ink/70 transition-colors hover:bg-mist/40"
            :class="inDrawer ? 'py-2.5' : 'py-2'"
            active-class="bg-mist/60 font-medium text-moss"
          >
            <NavIcon name="cupboard" :size="18" class="shrink-0" />
            <span class="truncate">Food review</span>
          </RouterLink>
        </li>
        <li>
          <RouterLink
            to="/admin/play"
            class="flex items-center gap-3 rounded-lg px-3 text-sm text-ink/70 transition-colors hover:bg-mist/40"
            :class="inDrawer ? 'py-2.5' : 'py-2'"
            active-class="bg-mist/60 font-medium text-moss"
          >
            <NavIcon name="repeat" :size="18" class="shrink-0" />
            <span class="truncate">Play catalogue</span>
          </RouterLink>
        </li>
        <li>
          <RouterLink
            to="/admin/restaurants"
            class="flex items-center gap-3 rounded-lg px-3 text-sm text-ink/70 transition-colors hover:bg-mist/40"
            :class="inDrawer ? 'py-2.5' : 'py-2'"
            active-class="bg-mist/60 font-medium text-moss"
          >
            <NavIcon name="cupboard" :size="18" class="shrink-0" />
            <span class="truncate">Restaurant menus</span>
          </RouterLink>
        </li>
      </ul>
    </div>
  </nav>
</template>
